import { ApolloError } from '@apollo/client';
import {
  ApolloLogo,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Group,
  Heading,
  Input,
  Link,
  Text,
} from '@apollo/orbit';
import * as CompanyEmailValidator from 'company-email-validator';
import { FormikErrors, FormikTouched, useFormik } from 'formik';
import React, { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import * as Yup from 'yup';

import * as routes from 'src/app/onboarding/routes';
import { Form } from 'src/components/common/form/Form';
import { Loading } from 'src/components/common/loading/Loading';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { PrivacyPolicyLink } from 'src/components/privacyPolicyLink/PrivacyPolicyLink';
import { TermsOfServiceLink } from 'src/components/termsOfServiceLink/TermsOfServiceLink';
import { useClearMeFromApolloCache } from 'src/hooks/useClearMeFromApolloCache';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useIdentity } from 'src/hooks/useIdentity';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { usePasswordChecker } from 'src/hooks/usePasswordChecker';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { EventCategory } from 'src/lib/analytics';
import { ampli } from 'src/lib/analytics/amplitude/vendor';
import { UserSegment } from 'src/lib/graphqlTypes/types';
import { useLDIdentityType } from 'src/lib/launchDarkly/LaunchDarklyProvider';
import { LaunchDarklyIdentityTypes } from 'src/lib/launchDarkly/types';

import { GithubSignupCTA } from '../../components';
import {
  useCreateOrg,
  useCreateUserOrSetDetails,
  useDefaultAccountDetails,
} from '../../hooks';
import { getLeadDetails } from '../../hooks/useMarketo';
import { EnterpriseTrialDetailsPage } from '../enterpriseTrialDetailsPage/EnterpriseTrialDetailsPage';
import { getSignupValidationSchema } from '../welcome/utils';

import { EnterpriseTrialContent } from './EnterpriseTrialContent';

const ENTERPRISE_TRIAL_PLAN_ID = 'sub-engine-ent-trial';

interface Values {
  email: string;
  password: string;
  name: string;
  acceptedTerms: boolean;
  signupUser?: string;
  createOrg?: string;
  orgInvite?: string[];
}

export interface Props {
  initialState?: Partial<Values>;
  initialTouched?: FormikTouched<Values>;
  initialErrors?: FormikErrors<Values>;
  validateOnMount?: boolean;
}

const ENTERPRISE_TRIAL_TOS =
  'https://apollographql.com/free-trial-tos-v2023.pdf';

export const EnterpriseTrialSignup = ({
  initialErrors,
  initialState,
  initialTouched,
  validateOnMount,
}: Props) => {
  const [isLoggingInGithub, setIsLoggingInGithub] =
    React.useState<boolean>(false);
  const { type } = useRouteParams(routes.signup);
  const location = useLocation();
  const history = useHistory();
  const passwordChecker = usePasswordChecker();
  const { me, isUser, meLoading } = useIdentity();
  const { createUserOrSetDetails, loading: isCreatingUser } =
    useCreateUserOrSetDetails({
      lead: getLeadDetails(ENTERPRISE_TRIAL_PLAN_ID),
    });
  const { createOrg, loading: isCreatingOrg } = useCreateOrg();
  const clearMeFromCache = useClearMeFromApolloCache();
  const trackCustomEvent = useTrackCustomEvent();
  const [_, setCurrentAccountId] = useCurrentAccountId();
  const [, setUtm] = useLocalStorage('utm');

  React.useEffect(() => {
    if (!type) return;

    // Set the default `referrer`
    setUtm((previousValue) => ({
      ...previousValue,
      referrer: previousValue?.referrer ?? type,
    }));
  }, [type, setUtm]);

  /**
   * Maintain a reference to the signed up user.
   *
   * This is helpful if any of the serially called mutations after the user
   * signup fails and needs to be retried.
   */
  const signupUserResultRef = React.useRef<UnwrapPromise<
    ReturnType<typeof createUserOrSetDetails>
  > | null>(null);

  const existingUser = isUser ? me : null;
  const shouldUsePassword = !existingUser;
  const validationSchema = getSignupValidationSchema({
    password: shouldUsePassword,
    acceptedTerms: true,
  });

  const {
    errors,
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    touched,
    setErrors,
    validateForm,
    isValid,
  } = useFormik<Yup.InferType<typeof validationSchema>>({
    initialValues: {
      password: '',
      email: existingUser?.email ?? '',
      name: existingUser?.fullName ?? '',
      acceptedTerms: false,
      signupUser: '',
      createOrg: '',
      orgInvite: [],
      ...initialState,
    },
    initialTouched,
    initialErrors,
    validate: (formValues) => {
      const newErrors: FormikErrors<Values> = {};

      if (shouldUsePassword) {
        if (!formValues.password) {
          newErrors.password = 'Please provide a password.';
        } else {
          const passwordStatus = passwordChecker(formValues.password);
          if (!passwordStatus.isSuccess) {
            newErrors.password = passwordStatus.errorMessage;
          }
        }
      }

      if (!formValues.email) {
        newErrors.email = 'Please provide your company email.';
      } else if (
        !CompanyEmailValidator.isCompanyEmail(formValues.email.toLowerCase()) ||
        !CompanyEmailValidator.isCompanyDomain(formValues.email.toLowerCase())
      ) {
        newErrors.email = 'You must sign up using a valid, company email.';
      }

      if (!formValues.name) {
        newErrors.name = 'Please provide your full name.';
      }

      if (!formValues.acceptedTerms) {
        newErrors.acceptedTerms =
          'You must accept Apollo’s Terms of Service and Privacy Policy';
      }
      return newErrors;
    },
    validationSchema,
    validateOnMount,
    enableReinitialize: true,
    async onSubmit(formValues) {
      if (signupUserResultRef.current?.isSuccess) return;
      try {
        // Run this mutation only if we've *unsuccessfully* run it before.
        signupUserResultRef.current = await createUserOrSetDetails({
          userId: existingUser?.id ?? null,
          fullName: formValues.name,
          email: formValues.email,
          password: formValues.password,
          userSegment: UserSegment.NOT_SPECIFIED,
        });

        if (!signupUserResultRef.current?.isSuccess) {
          setErrors(signupUserResultRef.current?.errors);

          return;
        }

        const userId = signupUserResultRef.current.userId;

        await createOrg({
          userId,
          values: {
            accountId: defaultAccountDetails.accountId,
            companyUrl: '',
            accountName: defaultAccountDetails.accountName,
          },
          planId: ENTERPRISE_TRIAL_PLAN_ID,
        }).then((results) => {
          if (!results.isSuccess) {
            throw results.error;
          } else {
            // fire ga4 event
            // the following sign_up_flow event will be replaced by the
            // studio_sign_up event, and will eventually be removed
            trackCustomEvent({
              action: 'sign_up_flow',
              // Old event, this style of category is deprecated
              category: 'Created account' as EventCategory,
              label: 'Studio sign up flow',
            });
            // new, more descriptive ga4 conversion event to provide more
            // detailed info for our paid ad campaigns
            trackCustomEvent({
              action: 'studio_sign_up',
              category: 'Created account' as EventCategory,
              label: 'Studio sign up flow',
              email: formValues.email,
            });

            ampli.registrationSuccess({
              RegistrationVariant: 'studio-direct',
              RegistrationPath: 'enterprise-trial-signup',
            });

            if (userId) clearMeFromCache({ userId });

            if (results.orgId) setCurrentAccountId(results.orgId);
            // push to home page
            history.push({
              pathname: '/',
            });
          }
        });
      } catch (err) {
        setErrors({
          createOrg:
            err instanceof ApolloError
              ? err.message
              : 'Failed to start your trial. Please try again.',
        });
        ampli.registrationError({
          RegistrationVariant: 'studio-direct',
          RegistrationPath: 'enterprise-trial-signup',
          'Error Message': (err as ApolloError).message,
        });
      }
    },
  });

  // when the passwordChecker is updated, trigger a re-validation
  useEffect(() => {
    validateForm();
  }, [passwordChecker, validateForm]);

  /**
   * Get the default account settings for this org id and person's name
   */
  const defaultAccountDetails = useDefaultAccountDetails({
    accountId: '',
    fullName: values.name,
    accountName: '',
  });
  const isProcessing =
    isCreatingOrg ||
    isCreatingUser ||
    isLoggingInGithub ||
    isSubmitting ||
    defaultAccountDetails.loading;
  const shouldDisableButton = isProcessing || !isValid;
  const launchDarklyIdentityType = useLDIdentityType();

  useEffect(() => {
    // Amplitude will be disabled by default until LaunchDarklyIdentityType
    // resolves.
    if (launchDarklyIdentityType !== LaunchDarklyIdentityTypes.unknown) {
      // me is undefined until useIdentity resolves, then convert to null if the user is unknown
      if (me !== undefined) {
        ampli.registrationOpened({
          RegistrationVariant: 'studio-direct',
          RegistrationPath: 'enterprise-trial-signup',
        });
      }
    }
  }, [launchDarklyIdentityType, me]);

  return (
    <>
      {meLoading ? (
        <Loading />
      ) : (
        <div className="grid min-h-screen sm:grid-cols-2">
          <div className="flex justify-end">
            <EnterpriseTrialContent>
              <div className="pb-10 pt-12 md:pb-28 md:pt-16">
                <div className="mb-10 flex md:mt-2">
                  <ApolloLogo
                    logoType="wordmark"
                    colorScheme="primary"
                    className="h-8"
                  />
                </div>
                <Heading size="3xl" className="mb-1">
                  Try Apollo Enterprise free
                </Heading>
                <Text variant="subtitle" className="mb-6">
                  Sign up for a 28-day Enterprise Trial. No credit card
                  required. Upgrade anytime.
                </Text>
                {errors.signupUser && (
                  <ErrorMessage>{errors.signupUser}</ErrorMessage>
                )}
                {errors.createOrg && (
                  <ErrorMessage className="my-4">
                    We were able to successfully register your email, but there
                    was an error creating your organization: {errors.createOrg}
                  </ErrorMessage>
                )}
                {!existingUser && (
                  <>
                    <section>
                      <GithubSignupCTA
                        className="w-full"
                        loggingIn={isLoggingInGithub}
                        setLoggingIn={setIsLoggingInGithub}
                        type={type}
                        callbackUrl={routes.welcome.pathFrom({
                          location,
                          fromRouteConfig: routes.signup,
                        })}
                      />
                    </section>

                    <Divider label="OR" className="my-10" />
                  </>
                )}
                <section>
                  <Form noValidate onSubmit={handleSubmit}>
                    <Group orientation="vertical" gap={4}>
                      <FormControl
                        isRequired
                        isInvalid={touched.email && !!errors.email}
                      >
                        <FormLabel>Company Email</FormLabel>
                        <Input
                          type="email"
                          name="email"
                          className="w-full"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.email}
                          isDisabled={isSubmitting || isCreatingOrg}
                        />
                        <FormErrorMessage>{errors.email}</FormErrorMessage>
                      </FormControl>
                      <FormControl
                        isRequired
                        isInvalid={touched.name && !!errors.name}
                      >
                        <FormLabel>Full name</FormLabel>
                        <Input
                          type="text"
                          name="name"
                          className="w-full"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.name}
                          isDisabled={isSubmitting || isCreatingOrg}
                        />
                        <FormErrorMessage>{errors.name}</FormErrorMessage>
                      </FormControl>
                      {shouldUsePassword ? (
                        <FormControl
                          isRequired
                          isInvalid={touched.password && !!errors.password}
                        >
                          <FormLabel>Password</FormLabel>
                          <Input
                            type="password"
                            name="password"
                            className="w-full"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.password}
                            isDisabled={isSubmitting || isCreatingOrg}
                          />
                          <FormErrorMessage>{errors.password}</FormErrorMessage>
                        </FormControl>
                      ) : null}
                      <FormControl
                        isRequired
                        isInvalid={
                          touched.acceptedTerms &&
                          (!!errors.acceptedTerms || !values.acceptedTerms)
                        }
                      >
                        <FormLabel className="sr-only">
                          Terms of Service and Privacy Policy
                        </FormLabel>
                        <Checkbox
                          data-testid="accepts-terms"
                          title="You agree to Apollo's Terms of Service and Privacy Policy"
                          name="acceptedTerms"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isChecked={values.acceptedTerms}
                          isDisabled={isSubmitting || isCreatingOrg}
                        >
                          You agree to Apollo’s{' '}
                          <TermsOfServiceLink href={ENTERPRISE_TRIAL_TOS} /> and{' '}
                          <PrivacyPolicyLink />
                        </Checkbox>
                        <FormErrorMessage>
                          {errors.acceptedTerms}
                        </FormErrorMessage>
                      </FormControl>
                    </Group>
                    <Button
                      type="submit"
                      isDisabled={shouldDisableButton}
                      isLoading={isSubmitting || defaultAccountDetails.loading}
                      size="lg"
                      className="mt-6 w-full"
                      variant="secondary"
                    >
                      Create Account
                    </Button>
                  </Form>
                </section>
                <Text className="mt-8 text-center sm:mt-12">
                  Have an account?{' '}
                  <Link
                    to={routes.login.locationFrom({
                      location,
                      fromRouteConfig: routes.welcome,
                    })}
                  >
                    Sign in
                  </Link>
                </Text>
              </div>
            </EnterpriseTrialContent>
          </div>
          <div className="flex place-items-center bg-black-200">
            <EnterpriseTrialContent>
              <EnterpriseTrialDetailsPage />
            </EnterpriseTrialContent>
          </div>
        </div>
      )}
    </>
  );
};
